import { useEffect, useMemo } from 'react'
import { applySkin, resolveAppearance } from '@/lib/skins'
import { applyCustomization, useDemoCustomization, type DemoCustomization } from '@/lib/customizer'
import { IS_DEMO, withPersonaLook } from '@/lib/demo'
import { CREATOR_LOOK } from '@/lib/creator'

/**
 * Applies the creator's skin to <html>: data-skin (colours/fonts),
 * data-scheme + data-theme + .dark (light/dark), and paints Telegram's
 * header and background to match. Returns the resolved appearance so the
 * animated background can use the same palette.
 *
 * In the demo build the persona's look (Hombre is darker) and the local
 * customizer (skin, background, colours, radius, type) are layered on top and
 * re-applied whenever they change. The real build applies the creator's
 * fixed look from src/config/creator.json (generated by `npm run sync`).
 */

export function useSkin() {
  const custom = useDemoCustomization()
  const overrides = useMemo<DemoCustomization>(() => (IS_DEMO ? withPersonaLook(custom) : CREATOR_LOOK), [custom])
  const appearance = useMemo(() => resolveAppearance(undefined, overrides), [overrides])
  const { skin } = appearance

  useEffect(() => {
    // Already applied in main.tsx; re-applied here in case another hook reset <html>
    applySkin(skin)
    const scheme = applyCustomization(overrides, skin.scheme === 'dark')
    if (scheme !== skin.scheme) {
      const root = document.documentElement
      root.setAttribute('data-scheme', scheme)
      root.setAttribute('data-theme', scheme)
      root.classList.toggle('dark', scheme === 'dark')
      root.classList.toggle('light', scheme === 'light')
    }
    const headerColor = overrides.bg ?? skin.headerColor
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', headerColor)

    const tg = window.Telegram?.WebApp
    // Custom header/background colours exist from Bot API 6.1, and only inside Telegram
    if (!tg?.initData || !tg.isVersionAtLeast?.('6.1')) return
    try {
      tg.setHeaderColor(headerColor)
      tg.setBackgroundColor(headerColor)
    } catch (err) {
      // Older Telegram clients don't support custom header colours
      console.warn('[useSkin] Telegram colours not applied', err)
    }
  }, [skin, overrides])

  return appearance
}
